import React,{useState,useMemo} from 'react';
import {motion} from 'framer-motion';
import AnalyticsChart from './AnalyticsChart';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiTrendingUp,FiTarget,FiDollarSign,FiPercent,FiAward,FiInfo}=FiIcons;

const ReturnPotential=({baseMetrics,timeRange,multiplier})=> {
  const [winRateIncrease,setWinRateIncrease]=useState(10); // Default 10%

  // Calculate historical performance improvements based on past data
  const projectedMetrics=useMemo(()=> {
    // Base historical data
    const currentWinRate=baseMetrics.winRate || 8.0; // 8% base win rate
    const avgStakeAmount=baseMetrics.avgNetStake || 787.50;
    const avgFeeRate=0.075; // 7.5% average fee
    const avgWinMultiplier=3.0; // 3x average payout
    const totalPredictions=baseMetrics.stakedPredictions || 125;
    
    // Calculate what the improved win rate would have been
    const improvedWinRate=currentWinRate + winRateIncrease;
    const newWins=Math.round(totalPredictions * (improvedWinRate / 100));
    const currentWins=Math.round(totalPredictions * (currentWinRate / 100));
    const additionalWins=newWins - currentWins;
    
    // Calculate additional returns from improved wins
    const additionalWinnings=additionalWins * avgStakeAmount * avgWinMultiplier;
    const currentTotalWinnings=currentWins * avgStakeAmount * avgWinMultiplier;
    const newTotalWinnings=currentTotalWinnings + additionalWinnings;
    
    // Calculate new ROI metrics
    const totalStaked=totalPredictions * (avgStakeAmount / (1 - avgFeeRate)); // Gross stake
    const totalFees=totalStaked * avgFeeRate;
    const principalReturned=(totalPredictions - newWins) * avgStakeAmount; // Losers get net principal back
    const totalReturned=newTotalWinnings + principalReturned;
    const newNetProfit=totalReturned - totalStaked;
    const newROIvsFees=(newNetProfit / totalFees) * 100;
    const newOverallROI=(newNetProfit / totalStaked) * 100;
    
    // Calculate the total BBWIN won (winnings minus original stakes)
    const totalBBWINWon=newTotalWinnings - (newWins * avgStakeAmount);
    const currentBBWINWon=currentTotalWinnings - (currentWins * avgStakeAmount);
    
    return {
      totalWins: newWins,
      roiVsFees: newROIvsFees,
      roiTokenAdjusted: newOverallROI,
      totalBBWINWon: totalBBWINWon,
      additionalWinnings: additionalWinnings,
      additionalWins: additionalWins,
      improvedWinRate: improvedWinRate,
      additionalBBWINWon: totalBBWINWon - currentBBWINWon
    };
  },[baseMetrics,winRateIncrease]);

  // Generate historical comparison data
  const chartData=useMemo(()=> {
    const labels=['Current (8%)','13% Win Rate','18% Win Rate','23% Win Rate','28% Win Rate'];
    
    const generateHistoricalData=(baseValue,currentWinRate=8)=> {
      const avgStakeAmount=baseMetrics.avgNetStake || 787.50;
      const avgWinMultiplier=3.0;
      const totalPredictions=baseMetrics.stakedPredictions || 125;
      
      return [8,13,18,23,28].map(winRate=> {
        const wins=Math.round(totalPredictions * (winRate / 100));
        const winnings=wins * avgStakeAmount * avgWinMultiplier;
        const bbwinWon=winnings - (wins * avgStakeAmount); // Net BBWIN gained
        
        if (baseValue === baseMetrics.totalWins) return wins;
        if (baseValue === baseMetrics.roiVsFees) {
          // Calculate ROI vs fees for this win rate
          const totalStaked=totalPredictions * (avgStakeAmount / 0.925);
          const totalFees=totalStaked * 0.075;
          const principalReturned=(totalPredictions - wins) * avgStakeAmount;
          const totalReturned=winnings + principalReturned;
          const netProfit=totalReturned - totalStaked;
          return (netProfit / totalFees) * 100;
        }
        if (baseValue === baseMetrics.roiTokenAdjusted) {
          // Calculate overall ROI for this win rate
          const totalStaked=totalPredictions * (avgStakeAmount / 0.925);
          const principalReturned=(totalPredictions - wins) * avgStakeAmount;
          const totalReturned=winnings + principalReturned;
          const netProfit=totalReturned - totalStaked;
          return (netProfit / totalStaked) * 100;
        }
        return bbwinWon; // Total BBWIN won
      });
    };

    return {
      totalWins: {
        labels,
        values: generateHistoricalData(baseMetrics.totalWins)
      },
      roiVsFees: {
        labels,
        values: generateHistoricalData(baseMetrics.roiVsFees)
      },
      roiTokenAdjusted: {
        labels,
        values: generateHistoricalData(baseMetrics.roiTokenAdjusted)
      },
      totalBBWINWon: {
        labels,
        values: generateHistoricalData(baseMetrics.totalWins * 45.50) // Using a base for BBWIN won calculation
      }
    };
  },[baseMetrics]);

  const metricCards=[
    {
      title: 'Total Wins (Historical)',
      current: Math.round(baseMetrics.stakedPredictions * 0.08),
      projected: projectedMetrics.totalWins,
      increase: projectedMetrics.additionalWins,
      icon: FiTarget,
      color: 'text-bull-yellow',
      unit: 'wins'
    },
    {
      title: 'ROI vs Fees (Historical)',
      current: baseMetrics.roiVsFees,
      projected: projectedMetrics.roiVsFees,
      increase: projectedMetrics.roiVsFees - baseMetrics.roiVsFees,
      icon: FiPercent,
      color: 'text-bull-red',
      unit: '%'
    },
    {
      title: 'Overall ROI (Historical)',
      current: baseMetrics.roiTokenAdjusted,
      projected: projectedMetrics.roiTokenAdjusted,
      increase: projectedMetrics.roiTokenAdjusted - baseMetrics.roiTokenAdjusted,
      icon: FiTrendingUp,
      color: 'text-bull-yellow',
      unit: '%'
    },
    {
      title: 'Additional BBWIN Won',
      current: (baseMetrics.totalWins || 10) * 135, // Estimated current BBWIN won
      projected: projectedMetrics.totalBBWINWon,
      increase: projectedMetrics.additionalBBWINWon,
      icon: FiDollarSign,
      color: 'text-bull-red',
      unit: 'BBWIN'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-bull-red/10 to-bull-yellow/10 border border-bull-red/20 rounded-bull p-6">
        <div className="flex items-center gap-3 mb-4">
          <SafeIcon icon={FiTarget} className="w-8 h-8 text-bull-yellow" />
          <div>
            <h2 className="font-heading text-2xl font-bold text-bull-white">
              Historical Return Analysis
            </h2>
            <p className="text-bull-light-gray">
              See what your returns would have been with improved win rates based on past performance
            </p>
          </div>
        </div>
        
        {/* Win Rate Increase Selector */}
        <div className="bg-bull-charcoal rounded-bull p-4 border border-bull-gray">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-bull-white font-bold text-lg mb-2">
                Historical Win Rate Improvement
              </h3>
              <p className="text-bull-light-gray text-sm">
                Analysis of what your {timeRange} returns would have been with improved accuracy
              </p>
            </div>
            
            <div className="bg-bull-gray rounded-bull p-4 min-w-[200px]">
              <div className="text-center mb-3">
                <span className="text-3xl font-bold text-bull-red">+{winRateIncrease}%</span>
                <div className="text-bull-light-gray text-sm">Win Rate Improvement</div>
                <div className="text-bull-yellow text-xs mt-1">
                  From {baseMetrics.winRate}% to {projectedMetrics.improvedWinRate}%
                </div>
              </div>
              
              <input
                type="range"
                min="5"
                max="20"
                step="5"
                value={winRateIncrease}
                onChange={(e)=> setWinRateIncrease(parseInt(e.target.value))}
                className="w-full h-3 bg-bull-charcoal rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, #D40934 0%, #D40934 ${((winRateIncrease - 5) / 15) * 100}%, #4A5568 ${((winRateIncrease - 5) / 15) * 100}%, #4A5568 100%)`
                }}
              />
              
              <div className="flex justify-between text-xs text-bull-light-gray mt-2">
                <span>+5%</span>
                <span>+10%</span>
                <span>+15%</span>
                <span>+20%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Historical Performance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metricCards.map((metric,index)=> (
          <motion.div
            key={metric.title}
            initial={{opacity: 0,y: 20}}
            animate={{opacity: 1,y: 0}}
            transition={{delay: index * 0.1}}
            className="bg-bull-gray rounded-bull p-6 border border-bull-charcoal hover:border-bull-red/40 transition-all duration-300 shadow-bull"
            whileHover={{scale: 1.02}}
          >
            <div className="flex items-center justify-between mb-4">
              <SafeIcon icon={metric.icon} className={`w-8 h-8 ${metric.color}`} />
              <div className="text-right">
                <div className="text-bull-yellow text-sm font-bold">
                  +{metric.increase >= 1 ? metric.increase.toFixed(0) : metric.increase.toFixed(1)}{metric.unit}
                </div>
                <div className="text-bull-light-gray text-xs">Additional</div>
              </div>
            </div>
            
            <h3 className="text-bull-light-gray text-sm font-medium mb-3">
              {metric.title}
            </h3>
            
            <div className="space-y-2">
              <div className="flex justify-between items-baseline">
                <span className="text-bull-light-gray text-xs">Actual:</span>
                <span className="text-bull-white font-medium">
                  {metric.current >= 1 ? metric.current.toFixed(0) : metric.current.toFixed(1)}{metric.unit}
                </span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-bull-light-gray text-xs">With +{winRateIncrease}%:</span>
                <span className={`text-xl font-bold ${metric.color}`}>
                  {metric.projected >= 1 ? metric.projected.toFixed(0) : metric.projected.toFixed(1)}{metric.unit}
                </span>
              </div>
            </div>
            
            {/* Progress bar showing improvement */}
            <div className="mt-4">
              <div className="w-full bg-bull-charcoal rounded-full h-2">
                <motion.div
                  className="bg-gradient-to-r from-bull-red to-bull-yellow h-2 rounded-full"
                  initial={{width: 0}}
                  animate={{width: `${Math.min((metric.increase / metric.current) * 100, 100)}%`}}
                  transition={{duration: 0.8, delay: index * 0.1}}
                />
              </div>
              <div className="text-center text-xs text-bull-light-gray mt-1">
                {((metric.increase / metric.current) * 100).toFixed(1)}% improvement
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Historical Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <AnalyticsChart
          type="bar"
          data={chartData.totalWins}
          title="Historical Wins by Win Rate"
          height={350}
        />
        <AnalyticsChart
          type="line"
          data={chartData.roiVsFees}
          title="Historical ROI vs Fees (%)"
          height={350}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <AnalyticsChart
          type="line"
          data={chartData.roiTokenAdjusted}
          title="Historical Overall ROI (%)"
          height={350}
        />
        <AnalyticsChart
          type="bar"
          data={chartData.totalBBWINWon}
          title="Historical BBWIN Won"
          height={350}
        />
      </div>

      {/* Enhanced Insights Section */}
      <div className="bg-bull-gray rounded-bull p-6 border border-bull-charcoal shadow-bull">
        <div className="flex items-center gap-3 mb-4">
          <SafeIcon icon={FiInfo} className="w-6 h-6 text-bull-yellow" />
          <h3 className="font-heading text-xl font-semibold text-bull-white">
            Historical Performance Analysis
          </h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-bull-charcoal rounded-bull p-4 border border-bull-red/20">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiAward} className="w-5 h-5 text-bull-red" />
              <h4 className="text-bull-white font-bold">Fee Efficiency</h4>
            </div>
            <p className="text-bull-light-gray text-sm">
              With {baseMetrics.winRate}% win rate, you generated {baseMetrics.roiVsFees.toFixed(0)}% ROI vs fees. 
              At +{winRateIncrease}% win rate, this would have been {projectedMetrics.roiVsFees.toFixed(0)}% ROI.
            </p>
          </div>
          
          <div className="bg-bull-charcoal rounded-bull p-4 border border-bull-yellow/20">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-bull-yellow" />
              <h4 className="text-bull-white font-bold">Payout Structure Impact</h4>
            </div>
            <p className="text-bull-light-gray text-sm">
              2-4x payout multiplier with principal protection creates exponential returns for small win rate improvements
            </p>
          </div>
          
          <div className="bg-bull-charcoal rounded-bull p-4 border border-bull-red/20">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiTarget} className="w-5 h-5 text-bull-red" />
              <h4 className="text-bull-white font-bold">Marginal Gains</h4>
            </div>
            <p className="text-bull-light-gray text-sm">
              Small accuracy improvements create massive returns due to the fee-efficient, high-payout structure
            </p>
          </div>
        </div>
        
        <div className="mt-6 p-4 bg-gradient-to-r from-bull-red/10 to-bull-yellow/10 rounded-bull border border-bull-yellow/20">
          <h4 className="text-bull-yellow font-bold mb-2">Historical Analysis Summary</h4>
          <p className="text-bull-white text-sm">
            Based on your {timeRange} performance data, improving your win rate by just {winRateIncrease}% would have 
            generated an additional <strong className="text-bull-yellow">
            {projectedMetrics.additionalBBWINWon.toFixed(0)} BBWIN
            </strong> in returns - a 
            <strong className="text-bull-red">
            {(((projectedMetrics.roiVsFees - baseMetrics.roiVsFees) / baseMetrics.roiVsFees) * 100).toFixed(0)}% improvement
            </strong> in your ROI vs fees ratio. This demonstrates how the platform's fee structure and payout multipliers 
            create substantial leverage for prediction accuracy improvements.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReturnPotential;